import React, { useRef, useEffect, useState, useMemo } from 'react';
import * as d3 from 'd3';

const styles = {
  card: {
    backgroundColor: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#111827',
    margin: 0,
  },
  subtitle: {
    fontSize: '13px',
    color: '#6b7280',
    margin: '4px 0 16px 0',
  },
  emptyState: {
    textAlign: 'center',
    padding: '48px 24px',
    color: '#9ca3af',
    fontSize: '14px',
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    backgroundColor: '#1f2937',
    color: '#f9fafb',
    padding: '10px 14px',
    borderRadius: '6px',
    fontSize: '12px',
    lineHeight: '1.5',
    zIndex: 9999,
    boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
    maxWidth: '220px',
  },
  legendRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '16px',
    marginTop: '12px',
    fontSize: '12px',
    color: '#6b7280',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
  },
};

const ZONE_COLORS = {
  A: { fill: 'rgba(239, 68, 68, 0.10)', label: 'Zone A (2-3\u03c3)', stroke: '#ef4444' },
  B: { fill: 'rgba(234, 179, 8, 0.10)', label: 'Zone B (1-2\u03c3)', stroke: '#eab308' },
  C: { fill: 'rgba(34, 197, 94, 0.10)', label: 'Zone C (0-1\u03c3)', stroke: '#22c55e' },
};

export default function ShewhartControlChart({ data }) {
  const svgRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);

  const hasData = useMemo(() => {
    return (
      data &&
      Array.isArray(data.dates) &&
      data.dates.length > 0 &&
      Array.isArray(data.xbar) &&
      data.xbar.length > 0
    );
  }, [data]);

  useEffect(() => {
    if (!hasData || !svgRef.current) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const width = 700;
    const height = 400;
    const margin = { top: 20, right: 40, bottom: 10, left: 50 };
    const gapBetweenPanels = 30;

    const xbarHeight = height * 0.65;
    const rangeHeight = height * 0.35;
    const xbarPlotH = xbarHeight - margin.top - gapBetweenPanels / 2;
    const rangePlotH = rangeHeight - margin.bottom - gapBetweenPanels / 2;
    const rangeTop = xbarHeight + gapBetweenPanels / 2;

    const n = data.dates.length;
    const xScale = d3
      .scalePoint()
      .domain(d3.range(n))
      .range([margin.left, width - margin.right])
      .padding(0.5);

    // -- X-bar panel --
    const xbarMin = Math.min(data.lcl_x, d3.min(data.xbar)) * 0.995;
    const xbarMax = Math.max(data.ucl_x, d3.max(data.xbar)) * 1.005;
    const yXbar = d3
      .scaleLinear()
      .domain([xbarMin, xbarMax])
      .range([margin.top + xbarPlotH, margin.top])
      .nice();

    const xbarGroup = svg.append('g');

    // Zone shading (symmetric around centerline)
    const zoneBands = [
      { yTop: data.ucl_x, yBot: data.sigma2_upper, zone: 'A' },
      { yTop: data.sigma2_upper, yBot: data.sigma1_upper, zone: 'B' },
      { yTop: data.sigma1_upper, yBot: data.centerline, zone: 'C' },
      { yTop: data.centerline, yBot: data.sigma1_lower, zone: 'C' },
      { yTop: data.sigma1_lower, yBot: data.sigma2_lower, zone: 'B' },
      { yTop: data.sigma2_lower, yBot: data.lcl_x, zone: 'A' },
    ];

    zoneBands.forEach(({ yTop, yBot, zone }) => {
      const clampTop = Math.max(yBot, Math.min(yTop, xbarMax));
      const clampBot = Math.max(yBot, xbarMin);
      if (clampTop <= clampBot) return;
      xbarGroup
        .append('rect')
        .attr('x', margin.left)
        .attr('width', width - margin.left - margin.right)
        .attr('y', yXbar(clampTop))
        .attr('height', yXbar(clampBot) - yXbar(clampTop))
        .attr('fill', ZONE_COLORS[zone].fill);
    });

    // Horizontal control lines
    const controlLines = [
      { value: data.ucl_x, color: '#ef4444', dash: '6,3', label: 'UCL' },
      { value: data.lcl_x, color: '#ef4444', dash: '6,3', label: 'LCL' },
      { value: data.centerline, color: '#2563eb', dash: '4,2', label: 'CL' },
      { value: data.sigma1_upper, color: '#eab308', dash: '2,4', label: '' },
      { value: data.sigma1_lower, color: '#eab308', dash: '2,4', label: '' },
      { value: data.sigma2_upper, color: '#f97316', dash: '3,3', label: '' },
      { value: data.sigma2_lower, color: '#f97316', dash: '3,3', label: '' },
    ];

    controlLines.forEach(({ value, color, dash, label }) => {
      xbarGroup
        .append('line')
        .attr('x1', margin.left)
        .attr('x2', width - margin.right)
        .attr('y1', yXbar(value))
        .attr('y2', yXbar(value))
        .attr('stroke', color)
        .attr('stroke-width', 1)
        .attr('stroke-dasharray', dash)
        .attr('opacity', 0.8);

      if (label) {
        xbarGroup
          .append('text')
          .attr('x', width - margin.right + 4)
          .attr('y', yXbar(value))
          .attr('dy', '0.35em')
          .attr('fill', color)
          .attr('font-size', '9px')
          .attr('font-weight', 600)
          .text(label);
      }
    });

    // X-bar data line
    const lineGen = d3
      .line()
      .x((_, i) => xScale(i))
      .y((d) => yXbar(d))
      .curve(d3.curveMonotoneX);

    xbarGroup
      .append('path')
      .datum(data.xbar)
      .attr('d', lineGen)
      .attr('fill', 'none')
      .attr('stroke', '#2563eb')
      .attr('stroke-width', 1.5);

    // Data points
    data.xbar.forEach((val, i) => {
      const isOoc = data.outOfControl && data.outOfControl[i];
      const cx = xScale(i);
      const cy = yXbar(val);

      xbarGroup
        .append('circle')
        .attr('cx', cx)
        .attr('cy', cy)
        .attr('r', isOoc ? 5 : 3)
        .attr('fill', isOoc ? '#ef4444' : '#2563eb')
        .attr('stroke', isOoc ? '#991b1b' : '#1e40af')
        .attr('stroke-width', isOoc ? 2 : 1)
        .attr('cursor', 'pointer')
        .on('mouseenter', (event) => {
          const status = isOoc ? 'OUT OF CONTROL' : 'In control';
          setTooltip({
            x: event.clientX + 12,
            y: event.clientY - 10,
            content: {
              date: data.dates[i],
              xbar: val,
              range: data.range ? data.range[i] : null,
              status,
              isOoc,
            },
          });
        })
        .on('mousemove', (event) => {
          setTooltip((prev) =>
            prev ? { ...prev, x: event.clientX + 12, y: event.clientY - 10 } : prev
          );
        })
        .on('mouseleave', () => setTooltip(null));

      // "!" marker for out-of-control points
      if (isOoc) {
        xbarGroup
          .append('text')
          .attr('x', cx)
          .attr('y', cy - 10)
          .attr('text-anchor', 'middle')
          .attr('fill', '#ef4444')
          .attr('font-size', '12px')
          .attr('font-weight', 700)
          .attr('pointer-events', 'none')
          .text('!');
      }
    });

    // X-bar Y axis
    xbarGroup
      .append('g')
      .attr('transform', `translate(${margin.left},0)`)
      .call(
        d3
          .axisLeft(yXbar)
          .ticks(5)
          .tickFormat((d) => d.toFixed(1))
      )
      .call((g) => g.select('.domain').attr('stroke', '#d1d5db'))
      .call((g) => g.selectAll('.tick line').attr('stroke', '#e5e7eb'))
      .call((g) => g.selectAll('.tick text').attr('fill', '#6b7280').attr('font-size', '10px'));

    // X-bar panel label
    xbarGroup
      .append('text')
      .attr('x', margin.left + 4)
      .attr('y', margin.top + 12)
      .attr('fill', '#374151')
      .attr('font-size', '11px')
      .attr('font-weight', 600)
      .text('X\u0304 Chart');

    // -- Range panel --
    const rangeGroup = svg.append('g').attr('transform', `translate(0,${rangeTop})`);

    const rangeMax = Math.max(data.ucl_r || 0, d3.max(data.range) || 0) * 1.15;
    const yRange = d3
      .scaleLinear()
      .domain([0, rangeMax])
      .range([rangePlotH, 0])
      .nice();

    const barWidth = Math.max(2, Math.min(16, (width - margin.left - margin.right) / n - 2));

    // Range bars
    data.range.forEach((val, i) => {
      rangeGroup
        .append('rect')
        .attr('x', xScale(i) - barWidth / 2)
        .attr('y', yRange(val))
        .attr('width', barWidth)
        .attr('height', rangePlotH - yRange(val))
        .attr('fill', val > (data.ucl_r || Infinity) ? '#ef4444' : '#60a5fa')
        .attr('rx', 1)
        .attr('opacity', 0.8)
        .attr('cursor', 'pointer')
        .on('mouseenter', (event) => {
          const isOoc = data.outOfControl && data.outOfControl[i];
          setTooltip({
            x: event.clientX + 12,
            y: event.clientY - 10,
            content: {
              date: data.dates[i],
              xbar: data.xbar[i],
              range: val,
              status: isOoc ? 'OUT OF CONTROL' : 'In control',
              isOoc,
            },
          });
        })
        .on('mousemove', (event) => {
          setTooltip((prev) =>
            prev ? { ...prev, x: event.clientX + 12, y: event.clientY - 10 } : prev
          );
        })
        .on('mouseleave', () => setTooltip(null));
    });

    // Range UCL line
    if (data.ucl_r != null) {
      rangeGroup
        .append('line')
        .attr('x1', margin.left)
        .attr('x2', width - margin.right)
        .attr('y1', yRange(data.ucl_r))
        .attr('y2', yRange(data.ucl_r))
        .attr('stroke', '#ef4444')
        .attr('stroke-width', 1)
        .attr('stroke-dasharray', '6,3')
        .attr('opacity', 0.8);

      rangeGroup
        .append('text')
        .attr('x', width - margin.right + 4)
        .attr('y', yRange(data.ucl_r))
        .attr('dy', '0.35em')
        .attr('fill', '#ef4444')
        .attr('font-size', '9px')
        .attr('font-weight', 600)
        .text('UCL');
    }

    // Range Y axis
    rangeGroup
      .append('g')
      .attr('transform', `translate(${margin.left},0)`)
      .call(
        d3
          .axisLeft(yRange)
          .ticks(4)
          .tickFormat((d) => d.toFixed(1))
      )
      .call((g) => g.select('.domain').attr('stroke', '#d1d5db'))
      .call((g) => g.selectAll('.tick line').attr('stroke', '#e5e7eb'))
      .call((g) => g.selectAll('.tick text').attr('fill', '#6b7280').attr('font-size', '10px'));

    // Range panel label
    rangeGroup
      .append('text')
      .attr('x', margin.left + 4)
      .attr('y', 12)
      .attr('fill', '#374151')
      .attr('font-size', '11px')
      .attr('font-weight', 600)
      .text('R Chart');

    // Shared X axis (date labels at bottom of range panel)
    const tickIndices = n <= 15 ? d3.range(n) : d3.range(n).filter((_, i) => i % Math.ceil(n / 12) === 0 || i === n - 1);

    rangeGroup
      .append('g')
      .attr('transform', `translate(0,${rangePlotH})`)
      .call(
        d3
          .axisBottom(xScale)
          .tickValues(tickIndices)
          .tickFormat((i) => {
            const d = data.dates[i];
            if (!d) return '';
            const parts = d.split('-');
            if (parts.length >= 3) return `${parts[1]}/${parts[2]}`;
            return d;
          })
      )
      .call((g) => g.select('.domain').attr('stroke', '#d1d5db'))
      .call((g) => g.selectAll('.tick line').attr('stroke', '#e5e7eb'))
      .call((g) =>
        g
          .selectAll('.tick text')
          .attr('fill', '#6b7280')
          .attr('font-size', '9px')
          .attr('transform', 'rotate(-35)')
          .attr('text-anchor', 'end')
      );
  }, [data, hasData]);

  if (!hasData) {
    return (
      <div style={styles.card}>
        <h3 style={styles.title}>Shewhart Control Chart</h3>
        <p style={styles.subtitle}>Statistical process control for pricing stability</p>
        <div style={styles.emptyState}>No control chart data available.</div>
      </div>
    );
  }

  const oocCount = data.outOfControl ? data.outOfControl.filter(Boolean).length : 0;

  return (
    <div style={styles.card}>
      <h3 style={styles.title}>Shewhart Control Chart</h3>
      <p style={styles.subtitle}>Statistical process control for pricing stability</p>

      <svg
        ref={svgRef}
        viewBox="0 0 700 400"
        style={{ width: '100%', height: 'auto', display: 'block' }}
      />

      <div style={styles.legendRow}>
        <span style={styles.legendItem}>
          <span
            style={{
              width: 12,
              height: 12,
              borderRadius: '50%',
              backgroundColor: '#2563eb',
              display: 'inline-block',
            }}
          />
          In control
        </span>
        <span style={styles.legendItem}>
          <span
            style={{
              width: 12,
              height: 12,
              borderRadius: '50%',
              backgroundColor: '#ef4444',
              display: 'inline-block',
            }}
          />
          Out of control ({oocCount})
        </span>
        <span style={styles.legendItem}>
          <span
            style={{
              width: 14,
              height: 8,
              backgroundColor: ZONE_COLORS.C.fill,
              border: `1px solid ${ZONE_COLORS.C.stroke}`,
              display: 'inline-block',
            }}
          />
          Zone C (0-1{'\u03c3'})
        </span>
        <span style={styles.legendItem}>
          <span
            style={{
              width: 14,
              height: 8,
              backgroundColor: ZONE_COLORS.B.fill,
              border: `1px solid ${ZONE_COLORS.B.stroke}`,
              display: 'inline-block',
            }}
          />
          Zone B (1-2{'\u03c3'})
        </span>
        <span style={styles.legendItem}>
          <span
            style={{
              width: 14,
              height: 8,
              backgroundColor: ZONE_COLORS.A.fill,
              border: `1px solid ${ZONE_COLORS.A.stroke}`,
              display: 'inline-block',
            }}
          />
          Zone A (2-3{'\u03c3'})
        </span>
      </div>

      {tooltip && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: 4 }}>{tooltip.content.date}</div>
          <div>
            X{'\u0304'}: <strong>{tooltip.content.xbar?.toFixed(2)}</strong>
          </div>
          {tooltip.content.range != null && (
            <div>
              Range: <strong>{tooltip.content.range.toFixed(2)}</strong>
            </div>
          )}
          <div
            style={{
              marginTop: 4,
              color: tooltip.content.isOoc ? '#fca5a5' : '#86efac',
              fontWeight: 600,
            }}
          >
            {tooltip.content.status}
          </div>
        </div>
      )}
    </div>
  );
}
